'use client';

import { TrustStrip } from '@hyphengroup/cms-components';
import type { CmsTrustStripComponent } from '$/(cms)/_modules/cmsService/cmsServiceGql/mapper/components/TrustStrip';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import { useClientContext } from '$/modules/context/client';

/**
 * Brand-gated wrapper for the M3a Trust Strip (CMS-1641). The block ships only
 * on SingSaver (SG) and MoneyHero (HK); money101 / moneymax render nothing.
 *
 * Trip-type set selection reads the active insurance type from the travel
 * quotes context that M2 maintains, so the strip depends on M2 being mounted on
 * the Provider Page (always true there). The presentational TrustStrip owns the
 * row layout; this wrapper owns brand gating, Single/Annual selection, and the
 * hide-when-empty rule. Standalone (a BlockMapper block) it adds a
 * `-mt-spacing/gap/y` div to cancel the inter-block gap; nested inside another
 * block via `inline` (the M3 Plan Tier Selector left column, CMS-1864) it skips
 * that compensation and renders the strip directly.
 */
export const TrustStripWrapper = (
  props: CmsTrustStripComponent & {
    /**
     * Rendered inside another block's layout (the M3 Plan Tier Selector left
     * column, CMS-1864) rather than as a standalone BlockMapper block. Skips
     * the `-mt` gap-compensation hack, which only applies to the standalone
     * inter-block gap.
     */
    inline?: boolean;
  },
) => {
  const { brand } = useClientContext();
  const {
    pageData: { travelQuotes },
  } = usePageDataContext();

  if (brand !== 'singsaver' && brand !== 'moneyhero') {
    return null;
  }

  const activeTripType = travelQuotes?.params?.insuranceType ?? 'SINGLE_PLAN';
  const activeItems =
    activeTripType === 'ANNUAL_PLAN' ? props.itemsAnnual : props.itemsSingle;

  if (!props.visible || activeItems.length === 0) {
    return null;
  }

  if (props.inline) {
    return <TrustStrip items={activeItems} />;
  }

  return (
    // HACK: compensate BlockMapper gap-spacing/gap/y
    <div className="-mt-spacing/gap/y">
      <TrustStrip items={activeItems} />
    </div>
  );
};
